// Enemy health bars (Settings › Display, owner-approved 2026-11-02). Render-only: reads Sim enemies, never changes them.
// Two instanced quads per bar (dark back plate + coloured fill), billboarded to the camera, drawn on top of the scene with
// no depth test. One draw call each, sized for the horde cap; nothing is allocated per frame.
//   off   no bars
//   elite elites and bosses / guardians only (always shown)
//   all   elites and bosses always, regular foes once they have taken damage (full-health swarms stay uncluttered)
import * as THREE from 'three/webgpu';
import type { Sim } from '../core/sim';
import type { BarMode } from '../app/settingsPrefs';

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _r = new THREE.Vector3(), _c = new THREE.Color();
const COL_FOE = new THREE.Color(0xff4a3a), COL_ELITE = new THREE.Color(0xffc23a), COL_BOSS = new THREE.Color(0xff3a9a);

export class EnemyBars {
  readonly back: THREE.InstancedMesh;
  readonly fill: THREE.InstancedMesh;
  mode: BarMode = 'elite';
  /** bar size multiplier (the view grows it on far camera presets so the bars stay readable) */
  sizeK = 1;
  private cap: number;

  constructor(scene: THREE.Scene, cap: number) {
    this.cap = Math.max(8, cap);
    const mat = (c: number, o: number) => {
      // both transparent: the transparent list is sorted by renderOrder, so the plate always draws first and the fill over it
      const m = new THREE.MeshBasicNodeMaterial({ color: c, transparent: true, opacity: o, depthTest: false, depthWrite: false, fog: false });
      m.toneMapped = false; return m;
    };
    this.back = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat(0x0a0612, 0.88), this.cap);
    // fill quad anchored at its left edge so a scale on x shrinks it toward the left
    this.fill = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1).translate(0.5, 0, 0), mat(0xffffff, 1), this.cap);
    this.fill.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 3), 3);
    for (const im of [this.back, this.fill]) { im.frustumCulled = false; im.count = 0; im.renderOrder = 50; scene.add(im); }
    this.fill.renderOrder = 51;
  }

  /** after the camera has moved this frame. ground.sample(x, z) = terrain height. */
  /** topOf(e) = height of the drawn model's top above its ground */
  /** shownOf(i) = how far enemy slot i has risen in (spawnIn.ts; the bar waits until the foe is mostly up) */
  update(sim: Sim, camera: THREE.Camera, ground: { sample(x: number, z: number): number }, topOf: (e: Sim['enemies'][number], sim: Sim) => number, shownOf?: (i: number) => number): void {
    let n = 0;
    if (this.mode !== 'off') {
      const q = camera.quaternion;
      _r.set(1, 0, 0).applyQuaternion(q); // screen-right in world space
      for (let i = 0; i < sim.enemies.length; i++) {
        const e = sim.enemies[i];
        if (!e.alive || n >= this.cap || e.mode < 0) continue; // (MAP FOES: no bar over an underground foe)
        if (shownOf && shownOf(i) < 0.7) continue; // still rising in (far camera presets)
        const boss = e.def.kind === 'boss' || e === sim.boss;
        const frac = e.maxHp > 0 ? Math.max(0, Math.min(1, e.hp / e.maxHp)) : 1;
        if (!boss && !e.elite && (this.mode !== 'all' || frac >= 0.999)) continue;
        const K = this.sizeK, w = (boss ? 3.2 : e.elite ? 1.5 * Math.min(1.6, e.scale) : 1.05) * K, h = (boss ? 0.2 : e.elite ? 0.15 : 0.12) * K;
        const top = ground.sample(e.x, e.z) + topOf(e, sim) + e.lift + e.gripY + (boss ? 0.6 : 0.35);
        // back plate (1 px-ish border all round)
        _p.set(e.x, top, e.z); _s.set(w + 0.08 * this.sizeK, h + 0.08 * this.sizeK, 1);
        this.back.setMatrixAt(n, _m.compose(_p, q as THREE.Quaternion, _s));
        // fill from the left edge
        _p.set(e.x - _r.x * w * 0.5, top - _r.y * w * 0.5, e.z - _r.z * w * 0.5); _s.set(Math.max(0.001, w * frac), h, 1);
        this.fill.setMatrixAt(n, _m.compose(_p, q as THREE.Quaternion, _s));
        this.fill.setColorAt(n, _c.copy(boss ? COL_BOSS : e.elite ? COL_ELITE : COL_FOE));
        n++;
      }
    }
    this.back.count = n; this.fill.count = n;
    if (n) { this.back.instanceMatrix.needsUpdate = true; this.fill.instanceMatrix.needsUpdate = true; this.fill.instanceColor!.needsUpdate = true; }
  }
  clear(): void { this.back.count = 0; this.fill.count = 0; }
}
